// 수비 계산 — **착탄 역산이 둘로 갈렸다** (2026-08-10 정본 개정)
//
//   rallying 단계 앵커 :  착탄 = 앵커 시각 + 남은 rallying + 적 roster 의 marching
//   행군  단계 앵커 :  착탄 = 앵커 시각 + 남은 행군
//
//   δ = 0.5초 (고정)
//   보낼 시점 = 착탄 + δ − 내 marching
//   ∴ 발사 순간에 적 착탄까지 남은 시간 = 내 marching − δ        ← 상수
//
// **둘째 식에 적 marching 이 없다는 것이 이 개정의 전부다.** 행군 중에 읽은 잔여
// 시간이 곧 착탄까지 남은 시간이기 때문이고, 결과가 셋이다.
//
//   - marching 을 아직 못 모은 적 roster 도 착탄이 확정된다. 「착탄 미상」은 수비에서
//     가장 아픈 실패인데 이 모드가 그것을 우회하는 유일한 경로다.
//   - 명부의 적 marching 이 실제보다 짧게 적힌 위험 — 착탄 추정이 일러져 squad 가
//     착탄 전에 도착해 return 하는 그 위험 — 이 그 행에서만 사라진다.
//   - 같은 rally 를 rallying 으로 앵커해 두었어도 행군 중에 다시 앵커하면 더 정확해진다.
//     재앵커는 잘못 읽은 값의 대응 수단일 뿐 아니라 정확도를 올리는 수단이다.
//
// **과장하지 않는다.** 남은 행군이 내 marching 보다 짧으면 보낼 시점은 이미 지났고 그
// 조합은 여전히 불가다. 이 모드는 「모른다」를 「안다」로 바꾸지 「늦었다」를 「늦지
// 않았다」로 바꾸지 않는다.
//
// δ 를 더하는 방향으로 틀리면 squad 가 착탄 0.5초 **전에** 도착해 수용량이 차 있는
// 성에 그냥 return 한다. 그것이 이 앱이 고치려는 수비 실패 그 자체다.
//
// 구현 계획서는 사건 문서라 옛 식 한 줄만 알고 있다. 구속력은 정본이 이긴다.

import {
  addDuration,
  type Duration,
  duration,
  type Instant,
  instant,
  plus,
  toMillis,
} from '../time/brand.ts'

/** 적 착탄 **직후**에 도착하기 위한 여유. 고정값이고 **빼는 방향**이다. */
export const DEFENSE_DELTA: Duration = duration(500)

/** 적 rally 를 관측한 순간 그것이 어느 단계에 있었는가. */
export type EnemyPhase = 'rallying' | 'marching'

/**
 * 관측 앵커. **입력을 확정한 순간을 서버가 찍는다** — CT 는 시계를 읽지 않는다.
 *
 * 단계가 앵커에 들어오는 이유는 같은 숫자가 단계마다 다른 뜻이기 때문이다. rallying
 * 중의 `0:52` 는 아직 출발하지 않았다는 뜻이고, 행군 중의 `0:52` 는 이미 날아오고
 * 있다는 뜻이다. 둘을 구별하지 않으면 착탄이 적 marching 하나만큼 통째로 어긋난다.
 */
export interface EnemyAnchor {
  readonly phase: EnemyPhase
  readonly at: Instant
  /** 그 순간에 인게임 카운트다운이 보여 준 잔여 시간. */
  readonly remaining: Duration
}

export function rallyingAnchor(at: Instant, remaining: Duration): EnemyAnchor {
  return { phase: 'rallying', at, remaining }
}

export function marchingAnchor(at: Instant, remaining: Duration): EnemyAnchor {
  return { phase: 'marching', at, remaining }
}

/**
 * 적 rally 의 착탄을 역산한다. 명부에 marching 이 없으면 `undefined` — 「착탄 미상」이다.
 *
 * **행군 단계 앵커에서는 `enemyMarching` 을 읽지 않는다.** 인자로 받기는 하지만 그
 * 갈래에서 값이 식에 들어갈 자리가 없다. 호출부가 명부를 들고 오든 안 들고 오든, 명부에
 * 무엇이 적혀 있든 결과가 같아야 한다 — 그것이 이 개정이 실제로 반영됐다는 뜻이다.
 */
export function enemyImpact(
  anchor: EnemyAnchor,
  enemyMarching: Duration | undefined,
): Instant | undefined {
  if (anchor.phase === 'marching') {
    return addDuration(anchor.at, anchor.remaining)
  }
  if (enemyMarching === undefined) return undefined
  return addDuration(anchor.at, plus(anchor.remaining, enemyMarching))
}

/**
 * roster 가 인게임에서 읽을 카운트다운이 rallying 것인가 행군 것인가.
 *
 * **값이 `'R'`·`'M'` 이었다 (2026-08-17 까지).** 화면의 칩이 그 글자를 그대로 찍었으니
 * 편했지만, 그 순간 **코어가 사람이 읽는 글자를 만드는 자리**가 됐다. 정본이 못박은 것은
 * 「단계 라벨을 라운드 코너 칩으로 표기한다」이고 그 라벨이 어느 언어에서 어떤 글자인지는
 * 화면의 몫이다 — 코어에 두면 언어를 바꿔도 안 따라오고, 애초에 바꿀 자리가 없다.
 *
 * 그래서 값이 **뜻**이 됐다. 이름을 새로 짓지 않고 위의 `EnemyPhase` 가 이미 쓰는 어휘를
 * 그대로 빌린다 — 같은 두 단계를 가리키는 말이 저장소에 두 벌이면 반드시 갈린다. 다만
 * 두 타입이 묻는 것은 다르다: 앵커의 것은 **관측한 순간** 적이 어느 단계였는가이고, 이것은
 * **보낼 순간** 적이 어느 단계일 것인가다.
 *
 * T6 개정이 오디오 런타임에서, 이 회차가 서버 거절에서 같은 일을 했다. 남은 것은 코어의
 * 개시 하한 문장 둘이고 그것은 T3 의 몫이다.
 */
export type TriggerPhase = 'rallying' | 'marching'

/**
 * 보낼 시점을 인게임 숫자로 환산한 것. roster 는 이 값이 화면에 뜨는 순간 버튼을 누른다.
 *
 * 앱 카운트다운(T−)이 있는데도 트리거를 병기하는 이유는 기준 시계가 게임 자신의
 * 카운트다운이기 때문이다. 앱은 "곧 온다" 를 미리 알리고, 행동의 기준은 트리거다.
 */
export interface Trigger {
  phase: TriggerPhase
  remaining: Duration
}

export interface SquadInput {
  anchor: EnemyAnchor
  /** 명부의 적 marching. 아직 못 모았으면 `undefined`. */
  enemyMarching: Duration | undefined
  /** squad 를 보낼 아군 roster 의 행군 시간. rally 의 marching 과 같은 값이다. */
  myMarching: Duration
  now: Instant
}

export type SquadPlan =
  | { status: 'impact-unknown' }
  | { status: 'impossible'; impact: Instant; sendAt: Instant; lateBy: Duration }
  | {
      status: 'ready'
      impact: Instant
      sendAt: Instant
      trigger: Trigger
      /** 지금부터 보낼 시점까지. 화면에서 흐른다. */
      countdown: Duration
    }

/**
 * 트리거를 고른다 — **보낼 순간에 적이 어느 단계에 있는가**로 갈린다.
 *
 * 앵커가 어느 단계에서 찍혔는가와 다른 질문이라는 것이 함정이다. 그러나 행군 단계
 * 앵커에서는 둘이 같은 답을 낸다: 적이 지금 이미 날고 있고 보낼 시점은 지금보다
 * 뒤이므로 그때도 여전히 행군 중이다. **그래서 행군 앵커 행에는 rallying 트리거가 나오지
 * 않는다** — 명부 숫자만 보면 그 조건을 만족하더라도 그렇다. rallying 을 내면 roster 는
 * 화면에 없는 숫자를 찾게 된다.
 */
function triggerFor(
  anchor: EnemyAnchor,
  myMarching: Duration,
  enemyMarching: Duration | undefined,
): Trigger {
  const marchTrigger: Trigger = {
    phase: 'marching',
    remaining: duration(toMillis(myMarching) - toMillis(DEFENSE_DELTA)),
  }
  if (anchor.phase === 'marching' || enemyMarching === undefined) return marchTrigger

  // 보낼 순간에 착탄까지 남은 시간(내 marching − δ)이 적 행군 시간보다 길면, 그때 적은
  // 아직 rallying 중이다. 그 잔여 rallying 이 roster 가 읽을 숫자다.
  const rallyingRemaining =
    toMillis(myMarching) - toMillis(enemyMarching) - toMillis(DEFENSE_DELTA)
  if (rallyingRemaining <= 0) return marchTrigger
  return { phase: 'rallying', remaining: duration(rallyingRemaining) }
}

/**
 * 적 rally 하나와 아군 roster 하나의 대응을 낸다.
 *
 * 불가를 숨기지 않는 이유: 보낼 시점이 이미 지난 조합은 CT 가 배정을 바꿀 근거다.
 * 얼마나 늦었는지(`lateBy`)를 함께 내는 이유도 같다 — 「불가」만 있으면 3초 늦은 것과
 * 3분 늦은 것이 화면에서 같아 보인다.
 */
export function planSquad(input: SquadInput): SquadPlan {
  const impact = enemyImpact(input.anchor, input.enemyMarching)
  if (impact === undefined) return { status: 'impact-unknown' }

  const sendAt = instant(
    toMillis(impact) + toMillis(DEFENSE_DELTA) - toMillis(input.myMarching),
  )
  const lateBy = duration(toMillis(input.now) - toMillis(sendAt))
  if (toMillis(lateBy) > 0) return { status: 'impossible', impact, sendAt, lateBy }

  return {
    status: 'ready',
    impact,
    sendAt,
    trigger: triggerFor(input.anchor, input.myMarching, input.enemyMarching),
    countdown: duration(-toMillis(lateBy)),
  }
}

/**
 * 착탄순 정렬. **관측 순서도 rallying 종료 순서도 아니다** — marching 이 짧은 rally 가
 * 추월하고, 그 어긋남이 이 역산의 존재 이유다.
 *
 * 착탄 미상 행은 맨 뒤로 보낸다. 존재는 보여 주되 순서는 주장하지 않는다 — 어딘가에
 * 끼워 넣으면 그 위치가 근거 없는 주장이 되고, 틀린 주장은 아무 주장도 하지 않는
 * 것보다 나쁘다.
 */
export function compareByImpact(
  a: { impact: Instant | undefined },
  b: { impact: Instant | undefined },
): number {
  if (a.impact === undefined) return b.impact === undefined ? 0 : 1
  if (b.impact === undefined) return -1
  return toMillis(a.impact) - toMillis(b.impact)
}
